import { useCallback, useMemo } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { useTrackView } from '#src/app/shared/hooks/tracking';

type Result = Readonly<{
	trackClick: () => void;
	trackDismiss: () => void;
}>;

function useBannerProperties(spikingPetitionId: string) {
	const {
		data: {
			petition: { id: petitionId },
		},
	} = usePetitionDetailsPageData();

	return useMemo(
		() => ({ context: 'petition', petition_id: petitionId, destination_petition_id: spikingPetitionId }),
		[petitionId, spikingPetitionId],
	);
}

/**
 * Fires once per mount, and again when the current or spiking petition changes (`useTrackView` only
 * resets on its deps, not on its properties). Call it only where the banner actually renders.
 */
export function useTrackSpikingPetitionBannerView(spikingPetitionId: string): void {
	const properties = useBannerProperties(spikingPetitionId);

	useTrackView('spiking_petition_banner_view', properties, [properties.petition_id, spikingPetitionId]);
}

export function useTrackSpikingPetitionBanner(spikingPetitionId: string): Result {
	const properties = useBannerProperties(spikingPetitionId);
	const track = useTracking();

	const trackClick = useCallback(() => {
		void track('spiking_petition_banner_click', properties);
	}, [track, properties]);

	const trackDismiss = useCallback(() => {
		void track('spiking_petition_banner_dismiss', properties);
	}, [track, properties]);

	return { trackClick, trackDismiss };
}
